import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    User,
    Bell,
    Moon,
    Sun,
    Globe,
    Shield,
    Loader2,
    Save,
    Key,
    Link2
} from 'lucide-react';
import { toast } from 'sonner';
import OAuthSettings from './OAuthSettings';
import ConnectionStatusDashboard from '../components/ConnectionStatusDashboard';

const TIMEZONES = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Australia/Sydney'
];

export default function Settings() {
    const { user, updateUser, api } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [profileForm, setProfileForm] = useState({
        name: '',
        timezone: 'UTC'
    });

    const [settings, setSettings] = useState({
        notifications_enabled: true,
        email_notifications: true,
        dark_mode: true
    });

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/settings');
            setSettings(prev => ({ ...prev, ...response.data }));
        } catch (error) {
            console.error('Failed to fetch settings:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        if (user) {
            setProfileForm({
                name: user.name || '',
                timezone: user.timezone || 'UTC'
            });
        }
        fetchSettings();
    }, [user, fetchSettings]);

    const handleProfileSave = async () => {
        setSaving(true);
        try {
            await updateUser(profileForm);
            toast.success('Profile updated');
        } catch (error) {
            console.error('Failed to update profile:', error);
            toast.error('Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const handleSettingsSave = async () => {
        setSaving(true);
        try {
            await api.put('/settings', settings);
            toast.success('Settings saved');
        } catch (error) {
            console.error('Failed to save settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[60vh]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="settings-page">
            <div>
                <h1 className="text-3xl font-bold">Settings</h1>
                <p className="text-muted-foreground mt-1">
                    Manage your account and preferences
                </p>
            </div>

            <Tabs defaultValue="profile" className="space-y-6">
                <TabsList className="flex-wrap h-auto gap-1">
                    <TabsTrigger value="profile" data-testid="profile-tab">
                        <User className="mr-2 h-4 w-4" />
                        Profile
                    </TabsTrigger>
                    <TabsTrigger value="connections" data-testid="connections-tab">
                        <Link2 className="mr-2 h-4 w-4" />
                        Connections
                    </TabsTrigger>
                    {user?.role === 'admin' && (
                        <TabsTrigger value="oauth" data-testid="oauth-tab">
                            <Key className="mr-2 h-4 w-4" />
                            OAuth Config
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="preferences" data-testid="preferences-tab">
                        <Bell className="mr-2 h-4 w-4" />
                        Preferences
                    </TabsTrigger>
                    <TabsTrigger value="appearance" data-testid="appearance-tab">
                        <Moon className="mr-2 h-4 w-4" />
                        Appearance
                    </TabsTrigger>
                </TabsList>

                {/* Connection Status Dashboard */}
                <TabsContent value="connections" className="space-y-6">
                    <ConnectionStatusDashboard />
                </TabsContent>

                {/* OAuth Configuration (Admin Only) */}
                {user?.role === 'admin' && (
                    <TabsContent value="oauth" className="space-y-6">
                        <OAuthSettings />
                    </TabsContent>
                )}

                <TabsContent value="profile" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Profile Information
                            </CardTitle>
                            <CardDescription>
                                Update your personal information
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    value={user?.email || ''}
                                    disabled
                                    className="bg-muted"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Email cannot be changed
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="name">Full Name</Label>
                                <Input
                                    id="name"
                                    value={profileForm.name}
                                    onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                                    data-testid="name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="timezone">Timezone</Label>
                                <Select
                                    value={profileForm.timezone}
                                    onValueChange={(value) => setProfileForm(prev => ({ ...prev, timezone: value }))}
                                >
                                    <SelectTrigger data-testid="timezone-select">
                                        <SelectValue placeholder="Select timezone" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {TIMEZONES.map(tz => (
                                            <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button
                                onClick={handleProfileSave}
                                disabled={saving}
                                data-testid="save-profile-btn"
                            >
                                {saving ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Save className="mr-2 h-4 w-4" />
                                )}
                                Save Changes
                            </Button>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Shield className="h-5 w-5" />
                                Account Status
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                                <div>
                                    <p className="font-medium">Role</p>
                                    <p className="text-sm text-muted-foreground capitalize">
                                        {user?.role || 'member'}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="font-medium">Member Since</p>
                                    <p className="text-sm text-muted-foreground">
                                        {user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="preferences" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Bell className="h-5 w-5" />
                                Notifications
                            </CardTitle>
                            <CardDescription>
                                Configure how you receive notifications
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Push Notifications</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Receive in-app notifications
                                    </p>
                                </div>
                                <Switch
                                    checked={settings.notifications_enabled}
                                    onCheckedChange={(checked) =>
                                        setSettings(prev => ({ ...prev, notifications_enabled: checked }))
                                    }
                                    data-testid="notifications-switch"
                                />
                            </div>
                            <Separator />
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Email Notifications</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Receive email alerts for important events
                                    </p>
                                </div>
                                <Switch
                                    checked={settings.email_notifications}
                                    onCheckedChange={(checked) =>
                                        setSettings(prev => ({ ...prev, email_notifications: checked }))
                                    }
                                    data-testid="email-notifications-switch"
                                />
                            </div>
                            <Button
                                onClick={handleSettingsSave}
                                disabled={saving}
                                data-testid="save-preferences-btn"
                            >
                                {saving ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Save className="mr-2 h-4 w-4" />
                                )}
                                Save Preferences
                            </Button>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="appearance" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                                Appearance
                            </CardTitle>
                            <CardDescription>
                                Customize how KyberSocial looks
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Dark Mode</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Toggle between light and dark themes
                                    </p>
                                </div>
                                <Switch
                                    checked={theme === 'dark'}
                                    onCheckedChange={toggleTheme}
                                    data-testid="dark-mode-switch"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div
                                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                        theme === 'light' ? 'border-primary' : 'border-border'
                                    } bg-white text-black`}
                                    onClick={() => theme === 'dark' && toggleTheme()}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <Sun className="h-4 w-4" />
                                        <span className="font-medium">Light</span>
                                    </div>
                                    <div className="h-20 rounded bg-gray-100 flex items-center justify-center">
                                        <div className="w-12 h-2 bg-gray-300 rounded" />
                                    </div>
                                </div>
                                <div
                                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                        theme === 'dark' ? 'border-primary' : 'border-border'
                                    } bg-zinc-900 text-white`}
                                    onClick={() => theme === 'light' && toggleTheme()}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <Moon className="h-4 w-4" />
                                        <span className="font-medium">Dark</span>
                                    </div>
                                    <div className="h-20 rounded bg-zinc-800 flex items-center justify-center">
                                        <div className="w-12 h-2 bg-zinc-600 rounded" />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
